import { Check, Diamond, Info, Tag, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const PricingSection = () => {
  const prices = {
    annual: "497",
    monthly: "597",
  };

  const implOriginal = 3497;
  const implPrice = 2397;
  const implInstallment = implPrice / 12;
  const implDiscount = Math.round(((implOriginal - implPrice) / implOriginal) * 100);

  const links = {
    annual: "https://buy.stripe.com/3cIaEX9Qa4YceEabJ29k402",
    monthly:
      "https://buy.stripe.com/5kQdR91jE0HWfIefZi9k403",
  };

  const savings = Math.round(
    ((Number(prices.monthly) - Number(prices.annual)) / Number(prices.monthly)) * 100
  );

  const annualSavings = (Number(prices.monthly) - Number(prices.annual)) * 12;

  const features = [
    "Atendentes ilimitados",
    "Múltiplas conexões WhatsApp",
    "API Oficial sem complicações",
    "Templates aprovados pela Meta",
    "QR Code com custo zero por mensagem",
    "Chatbots com IA integrada",
    "Webhooks e automações avançadas",
    "Relatórios e dashboard completos",
    "Reescrita de mensagens com IA",
    "Suporte brasileiro prioritário",
  ];

  type PlanCardProps = {
    label: string;
    price: string;
    billingNote: string;
    badge?: string;
    highlighted?: boolean;
    ctaText: string;
    ctaHref: string;
    ctaVariant: "elite" | "light";
    delay?: number;
  };

  const PlanCard = ({
    label,
    price,
    billingNote,
    badge,
    highlighted,
    ctaText,
    ctaHref,
    ctaVariant,
    delay = 0,
  }: PlanCardProps) => (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.01 }}
      className={`relative glass-card p-6 md:p-8 rounded-2xl md:rounded-3xl flex flex-col ${
        highlighted ? "neon-border-purple" : "neon-border-cyan"
      }`}
    >
      {badge && (
        <div className="absolute -top-3 md:-top-4 left-1/2 -translate-x-1/2">
          <div
            className={`px-4 md:px-5 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-bold text-foreground whitespace-nowrap ${
              highlighted ? "bg-gradient-elite glow-purple" : "bg-success-green"
            }`}
          >
            {badge}
          </div>
        </div>
      )}

      <div className="text-center mb-6 pt-2">
        <p
          className={`text-[11px] md:text-xs font-bold uppercase tracking-wide mb-3 ${
            highlighted ? "text-electric-purple" : "text-cyber-cyan"
          }`}
        >
          {label}
        </p>

        <div className="flex items-center justify-center gap-2 mb-4">
          {highlighted ? (
            <Diamond size={22} className="md:w-6 md:h-6 text-electric-purple fill-electric-purple" />
          ) : (
            <Zap size={22} className="md:w-6 md:h-6 text-cyber-cyan fill-cyber-cyan" />
          )}
        </div>

        <div className="mb-1">
          <span className="text-muted-foreground text-base md:text-lg">R$</span>
          <span className="text-4xl md:text-6xl font-display font-bold text-foreground mx-1">
            {price}
          </span>
          <span className="text-muted-foreground text-sm md:text-base">/mês</span>
        </div>
        <p
          className={`text-xs md:text-sm ${
            highlighted ? "text-electric-purple" : "text-cyber-cyan"
          }`}
        >
          {billingNote}
        </p>
      </div>

      {/* Connection options */}
      <div className="grid grid-cols-2 gap-2 md:gap-3 mb-5 md:mb-6">
        <div className="p-3 rounded-xl bg-cyber-cyan/5 border border-cyber-cyan/20">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={14} className="text-cyber-cyan fill-cyber-cyan" />
            <span className="text-xs font-bold text-cyber-cyan">QR Code</span>
          </div>
          <p className="text-[10px] md:text-[11px] text-body-text leading-snug">
            Conexão imediata, sem custo por mensagem.
          </p>
        </div>
        <div className="p-3 rounded-xl bg-electric-purple/5 border border-electric-purple/20">
          <div className="flex items-center gap-2 mb-1">
            <Diamond size={12} className="text-electric-purple fill-electric-purple" />
            <span className="text-xs font-bold text-electric-purple">API Oficial</span>
          </div>
          <p className="text-[10px] md:text-[11px] text-body-text leading-snug">
            Risco mínimo de banimento seguindo as políticas da Meta.
          </p>
        </div>
      </div>

      <div className="mb-5 md:mb-6">
        <p className="text-xs md:text-sm text-muted-foreground mb-3">Tudo incluso:</p>
        <ul className="space-y-2">
          {features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <div
                className={`w-4 md:w-5 h-4 md:h-5 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0 ${
                  highlighted
                    ? "bg-electric-purple/20 text-electric-purple"
                    : "bg-cyber-cyan/20 text-cyber-cyan"
                }`}
              >
                <Check size={10} className="md:w-3 md:h-3" />
              </div>
              <span className="text-body-text text-xs md:text-sm">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-start gap-2 p-2 md:p-3 rounded-lg bg-muted/50 mb-4 md:mb-6">
        <Info size={14} className="md:w-4 md:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
        <p className="text-[10px] md:text-xs text-muted-foreground">
          Custo por mensagem conforme tabela da Meta, cobrado diretamente na sua conta Facebook
          Business Manager. <strong>Sem taxas escondidas.</strong>
        </p>
      </div>

      <Button
        variant={ctaVariant}
        size="lg"
        className="w-full text-sm md:text-base mt-auto"
        asChild
      >
        <a href={ctaHref} target="_blank" rel="noopener noreferrer">
          {ctaText}
        </a>
      </Button>
    </motion.div>
  );

  return (
    <section id="planos" className="py-16 md:py-24 relative" aria-labelledby="pricing-heading">
      {/* Background Effects */}
      <div className="absolute top-1/2 left-1/4 w-60 md:w-80 h-60 md:h-80 bg-cyber-cyan/10 rounded-full blur-[80px] md:blur-[100px]" />
      <div className="absolute top-1/2 right-1/4 w-72 md:w-96 h-72 md:h-96 bg-electric-purple/15 rounded-full blur-[80px] md:blur-[100px]" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-14"
        >
          <h2
            id="pricing-heading"
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-3 md:mb-4"
          >
            <span className="text-gradient-zavvi">Planos</span>
          </h2>
          <p className="text-base md:text-lg text-body-text px-4">
            Escolha como conectar seu WhatsApp: <strong>API Oficial Meta</strong> ou{" "}
            <strong>QR Code</strong>. Sem cobrança por atendente. Sem cobrança por número.
          </p>
        </motion.div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto items-start">
          <PlanCard
            label="Mensal"
            price={prices.monthly}
            billingNote="cobrado mensalmente"
            ctaText="Assinar mensal"
            ctaHref={links.monthly}
            ctaVariant="light"
          />
          <PlanCard
            label="Anual"
            price={prices.annual}
            billingNote="cobrado anualmente • parcele em até 12x"
            badge={`⭐ ECONOMIZE R$ ${annualSavings.toLocaleString("pt-BR")}/ano`}
            highlighted
            ctaText="Assinar anual"
            ctaHref={links.annual}
            ctaVariant="elite"
            delay={0.1}
          />
        </div>

        {/* Implementação (compartilhada) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-8 md:mt-10 max-w-3xl mx-auto"
        >
          <div className="p-4 md:p-5 rounded-xl bg-electric-purple/10 border border-electric-purple/20">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Tag size={14} className="text-electric-purple" />
              <span className="text-xs md:text-sm font-medium">Implementação guiada + suporte premium por 12 meses</span>
              <span className="bg-success-green/20 text-success-green text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full">
                -{implDiscount}%
              </span>
            </div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-muted-foreground line-through text-sm">
                R$ {implOriginal.toLocaleString("pt-BR")}
              </span>
              <span className="text-electric-purple font-bold text-lg">
                12x de R$ {implInstallment.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-10 md:mt-16 max-w-3xl mx-auto text-center"
        >
          <div className="glass-card rounded-xl md:rounded-2xl p-4 md:p-6 border border-electric-purple/20">
            <h3 className="font-display font-bold text-base md:text-lg mb-2 md:mb-3 text-gradient-zavvi">
              Por que a Zavvi?
            </h3>
            <p className="text-body-text text-xs md:text-sm leading-relaxed">
              A concorrência{" "}
              <span className="text-destructive font-semibold">
                não oferece cadastro ilimitado de agentes
              </span>{" "}
              nem
              <span className="text-destructive font-semibold"> conexão ilimitada de números</span>.
              Na Zavvi você tem{" "}
              <span className="text-cyber-cyan font-semibold">assentos ilimitados</span>,
              <span className="text-electric-purple font-semibold">
                {" "}
                segurança total com API Oficial
              </span>{" "}
              e
              <span className="text-cyber-cyan font-semibold"> agilidade máxima via QR Code</span>.
            </p>
          </div>
        </motion.div>

        <p className="text-center text-muted-foreground mt-6 md:mt-8 text-xs md:text-sm">
          ✓ Suporte brasileiro &nbsp;•&nbsp; ✓ Atendentes ilimitados &nbsp;•&nbsp; ✓ Números
          ilimitados
        </p>

        <p className="text-center text-muted-foreground mt-3 md:mt-4 text-xs md:text-sm px-4">
          * Precisa de outra forma de pagamento (Pix, boleto, transferência)?{" "}
          <a
            href="https://wa.me/5511936180993"
            target="_blank"
            rel="noopener noreferrer"
            className="text-cyber-cyan font-semibold hover:underline"
          >
            Fale com a gente no WhatsApp
          </a>
          .
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
